import React, { useEffect, useState } from 'react';

import { keyframes } from '@emotion/react';
import { Box, Flex, Image, Loader, Text } from '@opendoor/bricks/core';

import imgixUrl from 'src/helpers/imgixUrl';

interface ExclusivesIntroProps {
  questionKey: string;
}

const fadeInOut = keyframes`
  from, 0% { 
    opacity: 0; 
    transform: translateX(-100px);
  }
  
  10% {
    opacity: 1; 
    transform: translateX(0px);
  }
  90% {
    opacity: 1; 
    transform: translateX(0px);
  }
  100% { 
    opacity: 0;
    transform: translateX(200px);
  }
`;

const fadeIn = keyframes`
  from, 0% { 
    opacity: 0; 
    transform: translateX(-100px);
  }
  
  100% {
    opacity: 1; 
    transform: translateX(0px);
  }
`;

const ExclusivesIntro: React.FC<ExclusivesIntroProps> = () => {
  const [step, setStep] = useState(1);

  useEffect(() => {
    setTimeout(() => {
      setStep(2);
    }, 3200);
  }, [step]);
  return step === 1 ? <FirstStep /> : <SecondStep />;
};

export default ExclusivesIntro;

const FirstStep = () => (
  <Flex
    mt={[0, null, -10]}
    flexDirection="column"
    justifyContent="center"
    gridGap={4}
    height="calc(100dvh - 224px)"
    textAlign="left"
    maxWidth={['100%', null, '560px']}
    animation={`${fadeInOut} 3.2s ease`}
  >
    <Box>
      <Image
        maxHeight={['48px', null, '72px']}
        maxWidth={['48px', null, '72px']}
        alt="Confetti"
        src={imgixUrl('2023-06-1687461861596-67991.png')}
      />
      <Text
        as="h1"
        fontSize={['s3', null, 's4']}
        fontWeight="medium"
        letterSpacing={['-1.4']}
        mb="5"
        mt="7"
        lineHeight={['140', null, '120']}
      >
        Great news! Your home qualifies for Opendoor Exclusives
      </Text>
      <Text color="neutrals90" maxWidth={['500px', null, '415px']} mb={5}>
        Only select homes are eligible
      </Text>
      <Loader />
    </Box>
  </Flex>
);

const SecondStep = () => {
  return (
    <Flex
      pb={[11, null, 0]}
      flexDirection="column"
      analyticsName="reception-fe-exclusives-intro"
      trackImpression={true}
      justifyContent="center"
      textAlign="left"
      maxWidth={['100%', null, '560px']}
      animation={`${fadeIn} 2s ease`}
    >
      <Box>
        <Text fontWeight="medium" color="neutrals90" fontSize="s1">
          With Exclusives
        </Text>
        <Text
          as="h1"
          fontSize={['s3', null, 's4']}
          fontWeight="medium"
          letterSpacing={['-1.4']}
          mb="5"
          mt="3"
          lineHeight={['140', null, '120']}
        >
          You can get a cash offer and potential higher offers from pre-approved buyers
        </Text>
        {/* @ts-expect-error letter spacing closer to design */}
        <Text fontSize="s2" letterSpacing="-.266px" lineHeight="140">
          No work or commitment needed.
        </Text>
      </Box>
      <Flex
        border="1px solid"
        borderColor="neutrals40"
        borderRadius="roundedSquare"
        p="5"
        mb="3"
        mt="8"
        justifyContent="space-between"
        alignItems="center"
      >
        <Image
          maxHeight="40px"
          maxWidth="40px"
          alt="Opendoor Logo"
          src={imgixUrl('2023-06-1687463394503-80482.png')}
        />
        <Box flex="1" pl="5">
          <Text fontWeight="medium">Opendoor</Text>
          <Text>Cash offer</Text>
        </Box>
        <Text fontWeight="medium">$XXX,XXX</Text>
      </Flex>
      <Flex
        border="1px solid"
        borderColor="neutrals40"
        borderRadius="roundedSquare"
        p="5"
        mb="3"
        alignItems="center"
        justifyContent="space-between"
      >
        <Avatar initials="SR" color="#D3E0AD" />
        <Box flex="1" pl="5">
          <Text fontWeight="medium">Buyer</Text>
          <Text color="brand50">Pre-approved</Text>
        </Box>
        <Text color="green50" fontWeight="medium">
          +$32,000
        </Text>
      </Flex>
      <Flex
        border="1px solid"
        borderColor="neutrals40"
        borderRadius="roundedSquare"
        p="5"
        alignItems="center"
        justifyContent="space-between"
      >
        <Avatar initials="AP" color="#B6D6E8" />
        <Box flex="1" pl="5">
          <Text fontWeight="medium">Buyer</Text>
          <Text color="brand50">Pre-approved</Text>
        </Box>
        <Text color="green50" fontWeight="medium">
          +$29,500
        </Text>
      </Flex>
      <Text color="neutrals90" fontSize="s0" textAlign="center" mt="7">
        The program is contingent on home details. Success is not guaranteed.
        <br />
        To verify continue to next steps
      </Text>
    </Flex>
  );
};

/**
 *
 * @param initials 2 letter initials
 */
const Avatar = ({ initials, color }: { initials: string; color: string }) => {
  return (
    <Box position="relative">
      <Flex
        width="40px"
        height="40px"
        background={color}
        borderRadius="roundedSquare"
        justifyContent="center"
        alignItems="center"
      >
        <Text fontSize="s1" fontWeight="medium">
          {initials}
        </Text>
      </Flex>
      <Image
        position="absolute"
        maxHeight="18px"
        right="-6px"
        bottom="-2px"
        maxWidth="18px"
        alt="Opendoor Logo"
        src={imgixUrl('2023-06-1687463782029-55983.png')}
      />
    </Box>
  );
};
